// The invoice drawer: lines grouped by production, what Xero says, every
// send attempt with its per-recipient result, the action history, and the
// four intents — approve & send (typed confirmation), resend, sync, PDF.

import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsivePicker } from '@/components/responsive-picker';
import { useEffect, useState } from 'react';
import { FailureNotice } from '@/components/failure-notice';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { Icons, MoneyCell } from '@/components/primitives';
import { SyncPill } from '@/components/sync-pill';
import { useInvoice, useInvoiceAction, useInvoicePdfUrl } from '@/api/invoicing-hooks';
import type { InvoiceDetailResponse, InvoicingSettings, InvoicePdfUrlResponse } from '@/api/types';
import { ago, fmtClock, fmtDay, fmtStampEt } from '@/lib/datetime';
import {
  actionKindLabel,
  EMAIL_STATUS_LABEL,
  STATE_LABEL,
  approveConfirmation,
  approveNeedsTyping,
  approveSendConfirmLabel,
  approveSendEffect,
  approveSendLabel,
  canApproveSend,
  canConfirmApprove,
  canOpenPdf,
  canResendEmail,
  canSyncXero,
  channelLabel,
  confirmationState,
  driftWarning,
  reportingUpdate,
  emailStatusBand,
  failureTone,
  formatCents,
  groupLinesByProduction,
  openAction,
  periodLabel,
  stateBand,
  xeroStatusBand,
  xeroStatusLabel,
  feeRateLabel,
  lineMediaCents,
  linesFeeTotal,
  adjustmentBanner,
  adjustmentChip,
  adjustmentLineView,
  canRaiseAdjustment,
  isAdjustment,
  refreezableAdjustment,
} from '@/lib/invoicing-view';
import type { AdjustmentBanner } from '@/lib/invoicing-view';
import { GroupRows } from './admin-invoices';
import { EmailAttempts, INVOICE_ACTIVITY_LIMIT, InvoiceActivity, InvoiceConfirm } from './admin-invoice-history';

export function InvoiceDrawer({
  id,
  settings,
  onClose,
}: {
  id: string;
  settings: InvoicingSettings | undefined;
  onClose: () => void;
}) {
  const query = useInvoice(id);
  const action = useInvoiceAction();
  const pdf = useInvoicePdfUrl();
  // One confirmation open at a time.
  const [confirm, setConfirm] = useState<'approve' | 'resend' | 'raise' | null>(null);
  const confirmOpen = confirm === 'approve';
  const [typed, setTyped] = useState('');
  const [pdfLink, setPdfLink] = useState<InvoicePdfUrlResponse | null>(null);
  const closeConfirm = () => {
    setConfirm(null);
    setTyped('');
  };

  useEffect(() => {
    setConfirm(null);
    setTyped('');
    setPdfLink(null);
    action.reset();
    pdf.reset();
    // Reset per invoice, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const detail: InvoiceDetailResponse | undefined = query.data;
  const refreezable = detail
    ? refreezableAdjustment(detail.invoice, detail.adjustments ?? [], settings, detail.actions)
    : null;

  return (
    <>

      <ResponsiveOverlay label="admin invoice drawer details" onClose={onClose} className="drawer" aria-label="Invoice">
        <div className="drawer-hd">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.1em' }}>
              INVOICE · {detail ? detail.invoice.reference : id}
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '.01em',
                textTransform: 'uppercase',
                margin: '4px 0 6px',
              }}
            >
              {detail ? periodLabel(detail.invoice.period) : 'Invoice'}
            </h2>
            {detail && isAdjustment(detail.invoice) && (
              <div style={{ marginBottom: 6 }}>
                <span className="chip" data-testid="adjustment-chip" style={{ fontSize: 11 }}>
                  {adjustmentChip(detail.invoice)}
                </span>
              </div>
            )}
            {detail && (
              <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                <SyncPill band={stateBand(detail.invoice.state)} label={STATE_LABEL[detail.invoice.state]} />
                <SyncPill
                  band={xeroStatusBand(detail.invoice.xero_status)}
                  label={xeroStatusLabel(detail.invoice.xero_status)}
                />
                <SyncPill
                  band={emailStatusBand(detail.invoice.email_status)}
                  label={EMAIL_STATUS_LABEL[detail.invoice.email_status]}
                />
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  TOTAL <MoneyCell value={detail.invoice.total_cents / 100} dp={2} /> · {detail.invoice.line_count}{' '}
                  {detail.invoice.line_count === 1 ? 'line' : 'lines'} · fee {feeRateLabel(detail.invoice.management_fee_bps)}
                </span>
              </div>
            )}
          </div>
          <button
            className="btn btn-sm btn-ghost"
            aria-label="Close invoice"
            onClick={onClose}
            style={{ fontSize: 18, padding: '4px 10px' }}
          >
            ×
          </button>
        </div>

        <div className="drawer-body">
          {query.isError && (
            <QueryErrorNote error={query.error} what="invoice" onRetry={() => void query.refetch()} />
          )}
          {!detail && !query.isError && <div className="muted">Loading invoice…</div>}
          {detail && <DrawerBody detail={detail} settings={settings} />}
        </div>

        {detail && (
          <div className="drawer-foot inv-foot">
            <div className="inv-foot-actions">
              {canApproveSend(detail.invoice, detail.actions) && (
                <button
                  className="btn btn-primary btn-sm"
                  disabled={action.isPending}
                  aria-expanded={confirmOpen}
                  onClick={() => setConfirm((open) => (open === 'approve' ? null : 'approve'))}
                >
                  <span>{Icons.send}</span>
                  {approveSendLabel(detail.invoice)}
                </button>
              )}
              {canResendEmail(detail.invoice, detail.actions) && (
                <button
                  className="btn btn-sm"
                  disabled={action.isPending}
                  aria-expanded={confirm === 'resend'}
                  onClick={() => setConfirm((open) => (open === 'resend' ? null : 'resend'))}
                >
                  Resend email
                </button>
              )}
              {canRaiseAdjustment(detail.invoice, settings, detail.actions) && (
                <button
                  className="btn btn-sm"
                  disabled={action.isPending}
                  aria-expanded={confirm === 'raise'}
                  onClick={() => setConfirm((open) => (open === 'raise' ? null : 'raise'))}
                >
                  Raise adjustment now
                </button>
              )}
              {refreezable && (
                <button
                  className="btn btn-sm"
                  disabled={action.isPending}
                  title="Rebuild it from the current config (contact, account code, tax type) and return it to review; nothing is sent"
                  onClick={() => action.mutate({ invoice_id: detail.invoice.id, kind: 'raise_adjustment' })}
                >
                  Re-freeze {refreezable.reference}
                </button>
              )}
              {canSyncXero(detail.invoice, detail.actions) && (
                <button
                  className="btn btn-sm"
                  disabled={action.isPending}
                  onClick={() => action.mutate({ invoice_id: detail.invoice.id, kind: 'sync_xero' })}
                >
                  <span>{Icons.refresh}</span>
                  Sync from Xero
                </button>
              )}
              {canOpenPdf(detail.invoice) && (
                <button
                  className="btn btn-sm"
                  disabled={pdf.isPending}
                  onClick={() =>
                    pdf.mutate(detail.invoice.id, {
                      onSuccess: (link) => {
                        setPdfLink(link);
                        window.open(link.url, '_blank', 'noopener,noreferrer');
                      },
                    })
                  }
                >
                  <span>{Icons.dl}</span>
                  PDF
                </button>
              )}
            </div>
            {confirmOpen && canApproveSend(detail.invoice, detail.actions) && (
              <ResponsivePicker kind="confirmation" label={approveSendLabel(detail.invoice)} onClose={closeConfirm}>
                <InvoiceConfirm
                  label={approveSendLabel(detail.invoice)}
                  confirmLabel={action.isPending ? 'Queuing…' : approveSendConfirmLabel(detail.invoice)}
                  confirmDisabled={!canConfirmApprove(detail.invoice, detail.actions, typed, action.isPending)}
                  onCancel={closeConfirm}
                  onConfirm={() => {
                    closeConfirm();
                    action.mutate({
                      invoice_id: detail.invoice.id,
                      kind: 'approve_send',
                      // The version on screen, so a re-freeze in between refuses.
                      expected_basis_fingerprint: detail.invoice.basis_fingerprint,
                    });
                  }}
                >
                  {approveNeedsTyping(detail.invoice) ? (
                    <>
                      <p>
                        This will {approveSendEffect(detail.invoice)}
                        {settings ? ` (${settings.recipient_count})` : ''}. Type{' '}
                        <code className="mono">{approveConfirmation(detail.invoice)}</code> to confirm.
                      </p>
                      <input
                        className="input mono"
                        aria-label={isAdjustment(detail.invoice) ? 'Type the reference to confirm' : 'Type the period to confirm'}
                        value={typed}
                        onChange={(event) => setTyped(event.target.value)}
                        aria-invalid={confirmationState(approveConfirmation(detail.invoice), typed) === 'mismatch'}
                      />
                      {confirmationState(approveConfirmation(detail.invoice), typed) === 'mismatch' && (
                        <p className="muted inv-confirm-hint">Not an exact match yet.</p>
                      )}
                    </>
                  ) : (
                    <p>This will {approveSendEffect(detail.invoice)}.</p>
                  )}
                </InvoiceConfirm>
              </ResponsivePicker>
            )}
            {confirm === 'resend' && canResendEmail(detail.invoice, detail.actions) && (
              <ResponsivePicker kind="confirmation" label="Resend email" onClose={closeConfirm}>
                <InvoiceConfirm
                  label="Resend email"
                  confirmLabel="Resend"
                  onCancel={closeConfirm}
                  onConfirm={() => {
                    closeConfirm();
                    action.mutate({ invoice_id: detail.invoice.id, kind: 'resend_email' });
                  }}
                >
                  <p>
                    Send the archived PDF again to the configured recipients
                    {settings ? ` (${settings.recipient_count})` : ''}, as a new email.
                  </p>
                </InvoiceConfirm>
              </ResponsivePicker>
            )}
            {confirm === 'raise' && canRaiseAdjustment(detail.invoice, settings, detail.actions) && (
              <ResponsivePicker kind="confirmation" label="Raise adjustment now" onClose={closeConfirm}>
                <InvoiceConfirm
                  label="Raise adjustment now"
                  confirmLabel="Raise"
                  onCancel={closeConfirm}
                  onConfirm={() => {
                    closeConfirm();
                    action.mutate({ invoice_id: detail.invoice.id, kind: 'raise_adjustment' });
                  }}
                >
                  <p>
                    Raise an adjustment for {formatCents(detail.invoice.basis_drift_total_cents ?? 0, true)} now,
                    without waiting for the run weekday? The settled-data checks still apply, and it is held for
                    your review; nothing is sent until you approve it.
                  </p>
                </InvoiceConfirm>
              </ResponsivePicker>
            )}
            {pdfLink && (
              <a href={pdfLink.url} target="_blank" rel="noopener noreferrer" className="inv-pdf-link">
                Open PDF (link valid until {fmtClock(pdfLink.expires_at)} ET) ↗
              </a>
            )}
            <MutationErrorLine error={action.error ?? pdf.error} />
          </div>
        )}
      </ResponsiveOverlay>
    </>
  );
}

function DrawerBody({
  detail,
  settings,
}: {
  detail: InvoiceDetailResponse;
  settings: InvoicingSettings | undefined;
}) {
  const { invoice, lines, emails, actions } = detail;
  const banner = adjustmentBanner(invoice, detail.adjustments ?? [], settings);
  // The adjustment banner says what the drift means for a sent week; the
  // generic drift notice stays for every other state.
  const drift = banner ? null : driftWarning(invoice);
  const reporting = reportingUpdate(invoice);
  const open = openAction(actions);
  const groups = groupLinesByProduction(lines);
  // The failure the invoice is in now, in words. An older response (no `failure`)
  // falls back to the stored text, and only while the invoice is failed.
  const current = invoice.failure ?? null;
  const currentFallback = invoice.state === 'failed' ? invoice.last_error : null;
  return (
    <>
      {(current || currentFallback) && (
        <div
          className={`inv-current-failure${failureTone(current?.code) === 'warn' ? ' is-warn' : ''}`}
          data-testid="invoice-current-failure"
        >
          <FailureNotice failure={current} fallback={currentFallback} tone={failureTone(current?.code)} />
        </div>
      )}
      {banner && <AdjustmentNotice banner={banner} />}
      {!drift && !banner && reporting && <div role="status" className="muted" style={{ padding: '10px 12px', marginBottom: 14, background: 'var(--bg-sunk)', fontSize: 11.5 }}>{reporting}</div>}
      {drift && (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            marginBottom: 14,
            background: 'color-mix(in oklch, var(--warn) 12%, transparent)',
            border: '1px solid color-mix(in oklch, var(--warn) 35%, var(--line))',
            borderLeft: '3px solid var(--warn)',
            borderRadius: 4,
            fontSize: 11.5,
          }}
        >
          <div style={{ fontWeight: 700 }}>■ {drift.headline}</div>
          <div className="muted" style={{ marginTop: 4 }}>{drift.detail}</div>
        </div>
      )}
      {open && (
        <div role="status" className="inv-progress" data-testid="invoice-progress">
          <b>{actionKindLabel(open, invoice)}</b> {open.status === 'queued' ? 'is waiting to start' : 'is in progress'}
          {' '}(asked {fmtStampEt(open.created_at)}). This updates on its own.
        </div>
      )}
      {invoice.warnings.length > 0 && (
        <ul
          className="mono"
          style={{
            margin: '0 0 14px',
            padding: '8px 12px 8px 28px',
            fontSize: 11,
            background: 'color-mix(in oklch, var(--warn) 10%, transparent)',
            border: '1px solid color-mix(in oklch, var(--warn) 35%, var(--line))',
            borderRadius: 4,
          }}
        >
          {invoice.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      <div className="field-lbl">Lines</div>
      {isAdjustment(invoice) ? (
        <AdjustmentLines lines={lines} totalCents={invoice.total_cents} />
      ) : (
      <div className="tbl-wrap" style={{ marginBottom: 14 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Channel</th>
              <th className="num">Impressions</th>
              <th className="num">Clicks</th>
              <th className="num">Media</th>
              <th className="num">Fee</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group.productionId}
                name={group.productionName}
                leading={4}
                media={group.mediaCents}
                fee={group.feeCents}
                subtotal={group.subtotalCents}
              >
                {group.lines.map((line) => (
                  <tr key={line.campaign_id}>
                    <td style={{ paddingLeft: 20 }}>{line.campaign_name}</td>
                    <td className="muted">{channelLabel(line.channel)}</td>
                    <td className="num mono">{line.impressions.toLocaleString()}</td>
                    <td className="num mono">{line.clicks.toLocaleString()}</td>
                    <td className="num">
                      <MoneyCell value={lineMediaCents(line) / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.fee_cents / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.spend_cents / 100} dp={2} />
                    </td>
                  </tr>
                ))}
              </GroupRows>
            ))}
            <tr>
              <td colSpan={4} style={{ fontWeight: 700 }}>Total</td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={(invoice.total_cents - linesFeeTotal(lines)) / 100} dp={2} />
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={linesFeeTotal(lines) / 100} dp={2} />
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={invoice.total_cents / 100} dp={2} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      )}

      <div className="field-lbl">Xero</div>
      <dl className="mono" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 14 }}>
        <dt className="muted">Invoice #</dt>
        <dd>{invoice.xero_invoice_number ?? '—'}</dd>
        <dt className="muted">Management fee</dt>
        <dd>{feeRateLabel(invoice.management_fee_bps)} of each campaign's gross, frozen when the invoice was generated</dd>
        <dt className="muted">Status</dt>
        <dd>{xeroStatusLabel(invoice.xero_status)}</dd>
        <dt className="muted">Amount due</dt>
        <dd>{invoice.xero_amount_due_cents != null ? formatCents(invoice.xero_amount_due_cents) : '—'}</dd>
        <dt className="muted">Amount paid</dt>
        <dd>{invoice.xero_amount_paid_cents != null ? formatCents(invoice.xero_amount_paid_cents) : '—'}</dd>
        <dt className="muted">Fully paid</dt>
        <dd>{invoice.xero_fully_paid_on ? fmtDay(invoice.xero_fully_paid_on) : '—'}</dd>
        <dt className="muted">Last synced</dt>
        <dd>{invoice.xero_last_synced_at ? `${ago(invoice.xero_last_synced_at)} · ${fmtStampEt(invoice.xero_last_synced_at)}` : '—'}</dd>
        {invoice.xero_online_url && (
          <>
            <dt className="muted">Online</dt>
            <dd>
              <a href={invoice.xero_online_url} target="_blank" rel="noopener noreferrer">
                View in Xero ↗
              </a>
            </dd>
          </>
        )}
      </dl>

      <div className="field-lbl">Emails ({emails.length})</div>
      <EmailAttempts emails={emails} />

      <div className="field-lbl">
        {actions.length >= INVOICE_ACTIVITY_LIMIT ? 'Recent activity' : 'Activity'} ({actions.length})
      </div>
      <InvoiceActivity actions={actions} invoice={invoice} />
    </>
  );
}

const BANNER_COLOR: Record<AdjustmentBanner['tone'], string> = {
  info: 'var(--accent, var(--ink))',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
};

/** Where a week stands against its settled basis, or which week an adjustment supplements. */
function AdjustmentNotice({ banner }: { banner: AdjustmentBanner }) {
  const color = BANNER_COLOR[banner.tone];
  return (
    <div
      role={banner.tone === 'info' ? 'status' : 'alert'}
      data-testid="adjustment-banner"
      style={{
        padding: '10px 12px',
        marginBottom: 14,
        background: `color-mix(in oklch, ${color} 10%, transparent)`,
        border: `1px solid color-mix(in oklch, ${color} 35%, var(--line))`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 4,
        fontSize: 11.5,
      }}
    >
      <div style={{ fontWeight: 700 }}>{banner.headline}</div>
      <div className="muted" style={{ marginTop: 4 }}>{banner.detail}</div>
    </div>
  );
}

/**
 * An adjustment's lines: per campaign, what was invoiced before (the week's
 * invoice plus earlier adjustments) against the settled final, and the
 * difference this adjustment bills as media plus fee.
 */
function AdjustmentLines({ lines, totalCents }: { lines: InvoiceDetailResponse['lines']; totalCents: number }) {
  const groups = groupLinesByProduction(lines);
  return (
    <div className="tbl-wrap" style={{ marginBottom: 14 }}>
      <table className="tbl" data-testid="adjustment-lines">
        <thead>
          <tr>
            <th>Campaign / channel</th>
            <th className="num">Impressions invoiced → final</th>
            <th className="num">Additional</th>
            <th className="num">Gross invoiced → final</th>
            <th className="num">Media</th>
            <th className="num">Fee</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <GroupRows
              key={group.productionId}
              name={group.productionName}
              leading={4}
              media={group.mediaCents}
              fee={group.feeCents}
              subtotal={group.subtotalCents}
            >
              {group.lines.map((line) => {
                const view = adjustmentLineView(line);
                return (
                  <tr key={line.campaign_id}>
                    <td style={{ paddingLeft: 20 }}>
                      {line.campaign_name} <span className="muted">· {channelLabel(line.channel)}</span>
                    </td>
                    <td className="num mono">
                      {view
                        ? `${view.invoicedImpressions.toLocaleString()} → ${view.finalImpressions.toLocaleString()}`
                        : '—'}
                    </td>
                    <td className="num mono">+{line.impressions.toLocaleString()}</td>
                    <td className="num mono">
                      {view ? `${formatCents(view.invoicedCents)} → ${formatCents(view.finalCents)}` : '—'}
                    </td>
                    <td className="num">
                      <MoneyCell value={lineMediaCents(line) / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.fee_cents / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.spend_cents / 100} dp={2} />
                    </td>
                  </tr>
                );
              })}
            </GroupRows>
          ))}
          <tr>
            <td colSpan={4} style={{ fontWeight: 700 }}>Adjustment total</td>
            <td className="num" style={{ fontWeight: 700 }}>
              <MoneyCell value={(totalCents - linesFeeTotal(lines)) / 100} dp={2} />
            </td>
            <td className="num" style={{ fontWeight: 700 }}>
              <MoneyCell value={linesFeeTotal(lines) / 100} dp={2} />
            </td>
            <td className="num" style={{ fontWeight: 700 }}>
              <MoneyCell value={totalCents / 100} dp={2} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
